import {
  MAutocomplete,
  MCheckbox,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import { MODES_OPTIONS } from "@app/types/generics.types";
import { FormControlLabel, Radio, RadioGroup, Grid, Typography, MenuItem, Select, InputLabel, FormControl } from "@mui/material";
import {
  useAnalysisTypes,
  useInstallation,
  useSamplingReason,
  useTypeSample,
  useUser,
} from "@services/servicesGenericsTables";
import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import DateRangeForm from "./subForms/dateRangeForm";
import DateRangeWeek from "./subForms/dateRangeWeek";
import { typesReports } from "../utils/constants";
import useFilteredSamplingPoints from "@app/hooks/useFilteredSamplingPoints";
import { InstallationTypes } from "@app/types/genericTables.types";
import dayjs from "dayjs";

interface ReportControlFormProps {
  mode: MODES_OPTIONS;
}

const FormControlReport = ({ mode }: ReportControlFormProps) => {
  const form = useFormContext();
  const [searchType, setSearchType] = useState<string>("dateRange");
  const { sampleTypeList } = useTypeSample();
  const { analysisTypeList } = useAnalysisTypes();

  const isView = mode === MODES_OPTIONS.VIEW;
  const { installationList } = useInstallation();
  const { samplingReasonList } = useSamplingReason();
  const { userList } = useUser();

  const installation = form.watch("report_installation");

  const filteredSamplingPoints = useFilteredSamplingPoints(
    installation as InstallationTypes,
    installation && "sampling_points"
  );

  const changeTypeDate = (type: string) => {
    if (type === "dateRange") {
      const today = dayjs();
      form.setValue("year_pickup", undefined);
      form.setValue("start_week_pickup", undefined);
      form.setValue("end_week_pickup", undefined);
      form.setValue("evo_start_date", today);
      form.setValue("evo_end_date", today);
    } else {
      form.setValue("evo_start_date", undefined);
      form.setValue("evo_end_date", undefined);
    }
    setSearchType(type);
  };
  return (
    <>
      <ResponsiveFormContainer columns={1} mb={4}>
        <MCheckbox
          key={JSON.stringify(form.getValues("report_lid"))}
          name="report_lid"
          options={typesReports}
          multiple={false}
          orientation="horizontal"
          columns={5}
          disabled={isView}
        />
      </ResponsiveFormContainer>
      <ResponsiveFormContainer columns={5} mb={2}>
        <MAutocomplete
          key={JSON.stringify(form.getValues("report_installation"))} //change this name when api is ready
          name="report_installation" //change this name when api is ready
          options={installationList}
          label="Instalación"
          getOptionLabel={(option) =>
            `${option.codigo} - ${option.descripcion}`
          }
          validation={{ required: "La instalación es requerida" }}
          disabled={isView}
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("reporte_tipos_analisis"))}
          name="reporte_tipos_analisis"
          options={analysisTypeList}
          label="Análisis"
          getOptionLabel={(option) => option.descripcion || ""}
          validation={{ required: "Debe seleccionar al menos un análisis" }}
          disabled={isView}
          multiple
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("motivo_muestreo_id"))} //change this name when api is ready
          name="motivo_muestreo_id" //change this name when api is ready
          options={samplingReasonList}
          label="Seleccione un motivo"
          getOptionLabel={(option) =>
            `${option.codigo} - ${option.descripcion}`
          }
          disabled={isView}
        />
        <MAutocomplete
          key={JSON.stringify(filteredSamplingPoints)}
          name="punto_muestreo_id"
          options={filteredSamplingPoints}
          label={"Punto de muestreo"}
          helperText={!installation ? "Seleccione una instalación" : ""}
          placeholder="Punto de muestreo"
          getOptionLabel={(option) =>
            `${option.codigo} - ${option.descripcion}`
          }
          disabled={!installation || isView}
        />
        <MAutocomplete
          key={JSON.stringify(form.getValues("tipo_muestra_id"))}
          name="tipo_muestra_id"
          options={sampleTypeList}
          label="Tipo de muestra"
          getOptionLabel={(option) =>
            `${option.codigo} - ${option.descripcion}`
          }
          disabled={isView}
        />

        <MDatePicker
          name="emission_date"
          pickerType="date"
          label="Fecha de emisión"
          validation={{
            required: "La fecha de emisión es requerida",
          }}
          disabled={isView}
        />
        <MAutocomplete
          name="approved_by"
          options={userList}
          label="Realizado por"
          getOptionLabel={(option) => option.username || ""}
          validation={{ required: "El analista es requerido" }}
          disabled={isView}
        />
      </ResponsiveFormContainer>
      <Controller
        name="searchType"
        control={form.control}
        defaultValue={searchType}
        render={({ field }) => (
          <RadioGroup
            {...field}
            value={field.value}
            onChange={(e) => {
              field.onChange(e.target.value);
              changeTypeDate(e.target.value);
            }}
            row
          >
            <FormControlLabel
              value="dateRange"
              control={<Radio />}
              label="Rango de fechas"
              disabled={isView}
            />
            <FormControlLabel
              value="dateRangeWeek"
              control={<Radio />}
              label="Rango de semana"
              disabled={isView}
            />
          </RadioGroup>
        )}
      />

      {form.getValues("searchType") === "dateRange" && (
        <DateRangeForm mode={mode} key={`form-${searchType}`} />
      )}
      {form.getValues("searchType") === "dateRangeWeek" && (
        <DateRangeWeek mode={mode} key={`form-${searchType}`} />
      )}
      
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Typography variant="tagText">{"Filtros de búsqueda"}</Typography>
        </Grid>
        
        {/* Filtro de Actividad */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>Actividad</Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel id="activity-comparator-label">Comparador</InputLabel>
            <Controller
              name="activity_comparator"
              control={form.control}
              defaultValue=""
              render={({ field }) => (
                <Select
                  {...field}
                  labelId="activity-comparator-label"
                  label="Comparador"
                  disabled={isView}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    if (e.target.value === "") {
                      form.setValue("activity_value", undefined);
                      form.setValue("activity_value_max", undefined);
                    } else if (e.target.value !== "between") {
                      form.setValue("activity_value_max", undefined);
                    }
                  }}
                >
                  <MenuItem value="">(Sin filtro)</MenuItem>
                  <MenuItem value="=">=</MenuItem>
                  <MenuItem value="<">&lt;</MenuItem>
                  <MenuItem value=">">&gt;</MenuItem>
                  <MenuItem value="<=">&le;</MenuItem>
                  <MenuItem value=">=">&ge;</MenuItem>
                  <MenuItem value="between">Entre (intervalo)</MenuItem>
                </Select>
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <MTextField
            name="activity_value"
            label={form.watch("activity_comparator") === "between" ? "Valor Mínimo" : "Valor"}
            placeholder="Ingrese valor"
            type="number"
            disabled={isView || !form.watch("activity_comparator")}
            fullWidth
          />
        </Grid>
        {form.watch("activity_comparator") === "between" && (
          <Grid item xs={12} sm={4}>
            <MTextField
              name="activity_value_max"
              label="Valor Máximo"
              placeholder="Ingrese valor máximo"
              type="number"
              disabled={isView}
              fullWidth
            />
          </Grid>
        )}
        
        {/* Filtro de Incertidumbre */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>Incertidumbre</Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel id="uncertainty-comparator-label">Comparador</InputLabel>
            <Controller
              name="uncertainty_comparator"
              control={form.control}
              defaultValue=""
              render={({ field }) => (
                <Select
                  {...field}
                  labelId="uncertainty-comparator-label"
                  label="Comparador"
                  disabled={isView}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    if (e.target.value === "") {
                      form.setValue("uncertainty_value", undefined);
                      form.setValue("uncertainty_value_max", undefined);
                    } else if (e.target.value !== "between") {
                      form.setValue("uncertainty_value_max", undefined);
                    }
                  }}
                >
                  <MenuItem value="">(Sin filtro)</MenuItem>
                  <MenuItem value="=">=</MenuItem>
                  <MenuItem value="<">&lt;</MenuItem>
                  <MenuItem value=">">&gt;</MenuItem>
                  <MenuItem value="<=">&le;</MenuItem>
                  <MenuItem value=">=">&ge;</MenuItem>
                  <MenuItem value="between">Entre (intervalo)</MenuItem>
                </Select>
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <MTextField
            name="uncertainty_value"
            label={form.watch("uncertainty_comparator") === "between" ? "Valor Mínimo" : "Valor"}
            placeholder="Ingrese valor"
            type="number"
            disabled={isView || !form.watch("uncertainty_comparator")}
            fullWidth
          />
        </Grid>
        {form.watch("uncertainty_comparator") === "between" && (
          <Grid item xs={12} sm={4}>
            <MTextField
              name="uncertainty_value_max"
              label="Valor Máximo"
              placeholder="Ingrese valor máximo"
              type="number"
              disabled={isView}
              fullWidth
            />
          </Grid>
        )}
        
        {/* Filtro de LID */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>LID</Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel id="lid-comparator-label">Comparador</InputLabel>
            <Controller
              name="lid_comparator"
              control={form.control}
              defaultValue=""
              render={({ field }) => (
                <Select
                  {...field}
                  labelId="lid-comparator-label"
                  label="Comparador"
                  disabled={isView}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    if (e.target.value === "") {
                      form.setValue("lid_value", undefined);
                      form.setValue("lid_value_max", undefined);
                    } else if (e.target.value !== "between") {
                      form.setValue("lid_value_max", undefined);
                    }
                  }}
                >
                  <MenuItem value="">(Sin filtro)</MenuItem>
                  <MenuItem value="=">=</MenuItem>
                  <MenuItem value="<">&lt;</MenuItem>
                  <MenuItem value=">">&gt;</MenuItem>
                  <MenuItem value="<=">&le;</MenuItem>
                  <MenuItem value=">=">&ge;</MenuItem>
                  <MenuItem value="between">Entre (intervalo)</MenuItem>
                </Select>
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <MTextField
            name="lid_value"
            label={form.watch("lid_comparator") === "between" ? "Valor Mínimo" : "Valor"}
            placeholder="Ingrese valor"
            type="number"
            disabled={isView || !form.watch("lid_comparator")}
            fullWidth
          />
        </Grid>
        {form.watch("lid_comparator") === "between" && (
          <Grid item xs={12} sm={4}>
            <MTextField
              name="lid_value_max"
              label="Valor Máximo"
              placeholder="Ingrese valor máximo"
              type="number"
              disabled={isView}
              fullWidth
            />
          </Grid>
        )}
      </Grid>
      
      <ResponsiveFormContainer columns={2} mt={2}>
        <MTextField
          name="comments"
          label="Comentarios"
          placeholder="Comentarios"
          type="textArea"
          rows={4}
          multiline
          fullWidth
          disabled={isView}
          step={0.01}
        />
      </ResponsiveFormContainer>
    </>
  );
};

export default FormControlReport;
